/**
 * Smooth scroll utility functions for enhanced user experience
 */

/**
 * Smooth scroll to a target element or position
 * @param {string|number} target - Element ID (with #) or scroll position
 * @param {Object} options - Scroll options
 */
export const smoothScrollTo = (target, options = {}) => {
    const {
        offset = 0,
        duration = 800,
        easing = 'easeInOutCubic'
    } = options;

    let targetPosition;

    if (typeof target === 'string') {
        const element = document.querySelector(target);
        if (!element) return;

        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        targetPosition = offsetPosition;
    } else {
        targetPosition = target;
    }

    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    let startTime = null;

    // Easing functions
    const easingFunctions = {
        linear: t => t,
        easeInQuad: t => t * t,
        easeOutQuad: t => t * (2 - t),
        easeInOutQuad: t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
        easeInCubic: t => t * t * t,
        easeOutCubic: t => (--t) * t * t + 1,
        easeInOutCubic: t => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1
    };

    const easingFunction = easingFunctions[easing] || easingFunctions.easeInOutCubic;

    function animation(currentTime) {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        const ease = easingFunction(progress);

        window.scrollTo(0, startPosition + distance * ease);

        if (timeElapsed < duration) {
            requestAnimationFrame(animation);
        }
    }

    requestAnimationFrame(animation);
};

/**
 * Smooth scroll to top of page
 */
export const scrollToTop = (options = {}) => {
    smoothScrollTo(0, options);
};

/**
 * Handle hash fragment on page load with retry mechanism
 */
export const handleInitialHash = () => {
    const hash = window.location.hash;
    if (!hash) return;

    let attempts = 0;
    const maxAttempts = 10;
    const retryDelay = 300;

    const scrollToHash = () => {
        const target = document.querySelector(hash);
        if (target) {
            // Ensure the element is visible and not in a loading state
            const rect = target.getBoundingClientRect();
            if (rect.height > 0) {
                setTimeout(() => {
                    smoothScrollTo(hash, { offset: 80 });
                    // Update URL without triggering navigation
                    history.replaceState(null, null, hash);
                }, 100);
                return true;
            }
        }
        return false;
    };

    const attemptScroll = () => {
        attempts++;
        if (scrollToHash()) {
            return; // Success
        }
        
        if (attempts < maxAttempts) {
            setTimeout(attemptScroll, retryDelay);
        } else {
            // Final fallback - try to scroll to any element with the ID
            console.warn(`Could not find target element for hash: ${hash}`);
        }
    };

    // Start attempting to scroll
    attemptScroll();
};

/**
 * Watch for section loading and handle hash navigation
 */
export const watchForSectionLoad = (sectionId, callback) => {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    const section = node.querySelector(`#${sectionId}`) || 
                                  (node.id === sectionId ? node : null);
                    if (section) {
                        callback(section);
                        observer.disconnect();
                    }
                }
            });
        });
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

    // Cleanup after 5 seconds
    setTimeout(() => observer.disconnect(), 5000);
};

/**
 * Add smooth scroll behavior to all anchor links
 */
export const initSmoothScrolling = () => {
    // Add smooth scroll behavior to CSS
    document.documentElement.style.scrollBehavior = 'smooth';

    // Handle anchor links
    document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href^="#"]');
        if (link) {
            e.preventDefault();
            const target = link.getAttribute('href');
            smoothScrollTo(target, { offset: 80 });
        }
    });

    // Handle browser back/forward with hash changes
    window.addEventListener('hashchange', () => {
        handleInitialHash();
    });
};

/**
 * Parallax scroll effect for background elements
 */
export const createParallaxEffect = (element, speed = 0.5) => {
    if (!element) return;

    const updateParallax = () => {
        const scrolled = window.pageYOffset;
        const parallax = scrolled * speed;
        element.style.transform = `translateY(${parallax}px)`;
    };

    window.addEventListener('scroll', updateParallax);
    return () => window.removeEventListener('scroll', updateParallax);
};